import type { EventPayload, Store } from 'effector'

import { WbsErrors } from '@bldsoft/cappie/api/wbs'
import { is } from '@bldsoft/cappie/rest'
import { api } from '@setplex/tria-api'
import { attach, createEvent, createStore, sample } from 'effector'
import { persist } from 'effector-storage/local'
import { debounce, not, previous } from 'patronum'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { model as sse } from '~/core/sse'
import { noop } from '~/shared/effector'
import { F, noop as noopFn, T } from '~/shared/helpers'

export const init = createEvent()

// get account effect
export const getAccountFx = attach({ effect: api.session.getAccountFx })

// session store
export const $session = api.session.$session

// previous session store
export const $previousSession = previous($session)

export const updateSubscriberName = createEvent<string>()
export const $subscriberName = createStore<string | null>(null).on(
  updateSubscriberName,
  (_, name) => name
)

sample({
  clock: $session,
  fn: (session) => session?.subscriberName ?? '',
  target: updateSubscriberName,
})

// store for a flag, indicating, that session has been restored
export const $ready = createStore<boolean>(false)

// event to separate regular account request failure from TOA `EC16`
const failedOrToa = createEvent<boolean>()

type Unauthorized = EventPayload<typeof api.events.http.unauthorized>

export const startGuestReloginTransition = createEvent()
export const stopGuestReloginTransition = createEvent()

export const $guestReloginPending = createStore(false)
  .on(startGuestReloginTransition, T)
  .on(stopGuestReloginTransition, F)

export const effectiveUnauthorized = createEvent<Unauthorized>()
export const routeUnauthorized = createEvent<Unauthorized>()

sample({
  clock: api.events.http.unauthorized,
  target: routeUnauthorized,
})

sample({
  clock: routeUnauthorized,
  source: $guestReloginPending,
  filter: (pending) => !pending,
  fn: (_, error) => error,
  target: effectiveUnauthorized,
})

// authentication flag
export const $authenticated = createStore<boolean>(false)
  .on($session, (__, session) => Boolean(session))
  .on(failedOrToa, (__, x) => x) // reset authentication flag on any request account error (except TOA)
  .on(api.session.signOutFx.finally, F) // in case of sign out
  .on(effectiveUnauthorized, F) // ignore stale 401s during logout -> guest relogin

// negative authentication flag
export const $notAuthenticated: Store<boolean> = not($authenticated)

// expired subscription flag
export const $expired = createStore<boolean>(false)
  .on($session, (__, session) => session?.currentSubscriptionActive === false)
  .on(
    api.session.signInFx.doneData,
    (__, { payload }) => payload.currentSubscriptionActive === false
  )
  .on(effectiveUnauthorized, F) // ignore stale 401s during logout -> guest relogin

// TOA accepted flag
export const $toaAccepted = createStore<boolean>(false)
  .on($session, (__, session) => session?.toa === null)
  .on(api.session.signInFx.doneData, (__, { payload }) => payload.toa === null)
  .on(api.events.logic.TOANotAccepted, F) // reset TOA flag on TOA not accepted error
  .on(effectiveUnauthorized, F) // ignore stale 401s during logout -> guest relogin
  .on(api.session.guestInFx.done, T) // TOA for guest is always like "accepted"

// TOA accepted flag
export const $hasToa = createStore<boolean>(false)
  .on(
    $session,
    (__, session) =>
      Boolean(session?.toaActive) &&
      session?.currentSubscriptionActive !== false
  )
  .on(
    api.session.signInFx.doneData,
    (__, { payload }) =>
      Boolean(payload.toaActive) && payload.currentSubscriptionActive !== false
  )
  .on(api.session.guestInFx.doneData, (__, { payload }) =>
    Boolean(payload.toaActive)
  )

// account number, will change on re-authenticate,
// or, for example, when change authentication from guest to subscriber
export const $accountNumber = createStore<string | null>(null).on(
  $session,
  (__, session) => session?.accountNumber ?? null
)

// save authentication flag in localStorage
// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $authenticated,
  key: 'tria__signed-in',
  sync: false, // do not synchronize this flag across tabs
  fail: noop,
})

// save account number in localStorage
// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $accountNumber,
  key: 'tria__account-number',
  fail: noop,
})

// in case of EC16 errorCode -> consider authentication goes right
sample({
  clock: getAccountFx.failData,
  fn: (data) => is(data, WbsErrors.TOANotAccepted),
  target: failedOrToa,
})

// in case of previously authenticated -> try to get account
// this will reset authentication flag in case of 401 response
sample({
  clock: init,
  filter: $authenticated,
  fn: () => ({ refresh: true }),
  target: getAccountFx,
})

// in case of NOT authenticated -> set session is ready right away
sample({
  clock: init,
  filter: $notAuthenticated,
  fn: T,
  target: $ready,
})

// on any response from getAccountFx -> set session is ready
sample({
  clock: getAccountFx.finally,
  fn: T,
  target: $ready,
})

// in case account number was changed (asynchronously from localStorage)
//   -> if changed to `null` - reset session
//   -> re-request account (and subscriber later)
//   -> and refresh current route (because account changing can cause many side-effects)

const accountNumberChanged = debounce({ source: $accountNumber, timeout: 500 })
const toaAcceptedChanged = debounce({ source: $toaAccepted, timeout: 500 })

sample({
  clock: accountNumberChanged,
  source: $session,
  filter: (session, accountNumber) =>
    accountNumber == null && //
    session?.accountNumber != null,
  target: api.session.reset,
})

sample({
  clock: [accountNumberChanged, toaAcceptedChanged],
  source: {
    session: $session,
    accountNumber: $accountNumber,
    toaAccepted: $toaAccepted,
  },
  filter: ({ session, toaAccepted, accountNumber }) =>
    accountNumber != null && //
    accountNumber !== session?.accountNumber &&
    toaAccepted,
  fn: noopFn,
  target: [getAccountFx, router.refresh],
})

//
// update session in SSE logic,
// to connect, disconnect or reconnect
//

sample({
  clock: $session,
  target: sse.session,
})

//
// register stores and events
//

register($session, '$entity/session')
